// libraries
import { Grid } from '@interstate/components/Grid';

import { formatDollarsAndCents } from '@makemydeal/dr-common-utils';
import * as compositeSelectors from '@makemydeal/dr-dash-store/selectors/compositeSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';

// consts/enums
import { CAP_COST_REDUCTION, CASH, CASH_FROM_CUSTOMER, REBATES, TRADE_IN } from '../../constants';

// components
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { createDealRootSelector } from '../../offerCompareColumn/hooks/createDealRootSelector';
import type { QuickCompareOffer } from '../../offerCompareColumn/types/quickCompareOffer';
import DealSummaryItem from '../common/DealSummaryItem';
import { DealSummaryItemWrapper } from '../common/DealSummaryItemWrapper';
import LineItemWithChildren from '../common/LineItemWithChildren';
import {
    labelStyles as defaultLabelStyles,
    valueStyles as defaultValueStyles,
    detailedViewGridProps,
    detailedViewLabelStyles,
    detailedViewSecondaryGridProps,
    detailedViewValueStyles,
    secondaryValueStyles
} from '../utils/styles';

import { useDealSummary } from '../../common/DealSummaryProvider';
import { useMediaQuery } from 'react-responsive';
import { BreakPoint } from '@makemydeal/dr-activities-common';
import { useTrackedSelector } from '../../telemetry/useTrackedSelector';
import { useHeldValue } from '../../hooks/useHeldSelector';

export interface ICapCostReductionProps {
    offer?: QuickCompareOffer;
    useWrapper?: boolean;
}

const CapCostReduction = ({ offer, useWrapper = true }: ICapCostReductionProps) => {
    const isEnhancedDealDetailsEnabled = useTrackedSelector(featureToggleSelectors.getEnhancedDealDetails, {
        component: 'CapCostReduction',
        field: 'enhancedDealDetails'
    });
    const useDealRootSelector = createDealRootSelector(offer ? { offer } : undefined, 'CapCostReduction');
    const isLargeSize = useMediaQuery({ query: `(min-width: ${BreakPoint.MEDIUM})` });
    const { showDetailedView } = useDealSummary();

    const netCashDown = useHeldValue(useDealRootSelector(offerReduxSelectors.getNetCashDown, 'netCashDown'), offer);
    const reductionRebate = useHeldValue(
        useDealRootSelector(offerReduxSelectors.getCapReductionRebate, 'capReductionRebate'),
        offer
    );
    const reductionTradeIn = useHeldValue(
        useDealRootSelector(offerReduxSelectors.getCapReductionTradeIn, 'capReductionTradeIn'),
        offer
    );
    const selectedCapCostReduction = useHeldValue(
        useDealRootSelector(compositeSelectors.getCapCostReduction, 'capCostReduction'),
        offer
    );

    let gridProps = {};
    if (showDetailedView && isLargeSize) {
        gridProps = {
            ...detailedViewGridProps,
            pt: '0.571rem'
        };
    } else {
        gridProps = detailedViewSecondaryGridProps;
    }
    const labelStyles = showDetailedView ? detailedViewLabelStyles : {};
    const valueStyles = showDetailedView ? secondaryValueStyles : {};

    return (
        <div>
            {isEnhancedDealDetailsEnabled ? (
                <DealSummaryItem
                    valueStyles={showDetailedView ? detailedViewValueStyles : defaultValueStyles}
                    labelStyles={showDetailedView ? {} : defaultLabelStyles}
                    label={CAP_COST_REDUCTION}
                    value={formatDollarsAndCents(selectedCapCostReduction)}
                >
                    <Grid {...gridProps}>
                        <LineItemWithChildren
                            label={CASH_FROM_CUSTOMER}
                            value={formatDollarsAndCents(netCashDown)}
                            labelStyles={labelStyles}
                            valueStyles={valueStyles}
                        />
                        <LineItemWithChildren
                            label={REBATES}
                            value={formatDollarsAndCents(reductionRebate)}
                            labelStyles={labelStyles}
                            valueStyles={valueStyles}
                        />
                        <LineItemWithChildren
                            label={TRADE_IN}
                            value={formatDollarsAndCents(reductionTradeIn)}
                            labelStyles={labelStyles}
                            valueStyles={valueStyles}
                        />
                    </Grid>
                </DealSummaryItem>
            ) : (
                <DealSummaryItemWrapper
                    label={CAP_COST_REDUCTION}
                    value={formatDollarsAndCents(selectedCapCostReduction)}
                    useWrapper={useWrapper}
                >
                    <Grid>
                        <LineItemWithChildren label={CASH} value={formatDollarsAndCents(netCashDown)} />
                        <LineItemWithChildren label={REBATES} value={formatDollarsAndCents(reductionRebate)} />
                        <LineItemWithChildren label={TRADE_IN} value={formatDollarsAndCents(reductionTradeIn)} />
                    </Grid>
                </DealSummaryItemWrapper>
            )}
        </div>
    );
};

export default CapCostReduction;
